import { THEME_CSS } from '../styles/themeVars';

export const THEME_STORAGE_KEY = 'seelay-theme';
const LEGACY_THEME_KEY = 'anuraghazra-theme';

export const FAVICON_PATHS = {
  light: '/favicon-light.svg',
  dark: '/favicon-dark.svg',
};

export const THEME_COLOR = {
  light: '#ffffff',
  dark: '#000000',
};

const cssVarBlock = mode => {
  const tokens = THEME_CSS[mode];
  return Object.entries(tokens)
    .map(([key, value]) => `--theme-${key}: ${value};`)
    .join('\n    ');
};

/** Override SSR light-theme styled-component rules before React hydrates. */
const preHydrationOverrides = `
  html[data-theme='dark'] [class*='ProjectsWrapper-sc-'],
  html[data-theme='dark'] [class*='DesignsWrapper-sc-'],
  html[data-theme='dark'] [class*='JsProjectsWrapper-sc-'],
  html[data-theme='dark'] [class*='OpenSourceWrapper-sc-'],
  html[data-theme='dark'] [class*='AboutBentoSection-sc-'],
  html[data-theme='dark'] [class*='BentoCanvas-sc-'],
  html[data-theme='dark'] [class*='FooterWrapper-sc-'],
  html[data-theme='dark'] [class*='RootWrapper-sc-'],
  html[data-theme='dark'] [class*='HomeWrapper-sc-'] {
    background-color: var(--theme-bg) !important;
    color: var(--theme-text) !important;
  }

  html[data-theme='dark'] [class*='AboutHeroTitle-sc-'],
  html[data-theme='dark'] [class*='AboutIntroText-sc-'],
  html[data-theme='dark'] [class*='PageHeaderTitle-sc-'],
  html[data-theme='dark'] [class*='PageHeaderLead-sc-'],
  html[data-theme='dark'] [class*='HeroHeadline-sc-'],
  html[data-theme='dark'] [class*='HeroGreeting-sc-'],
  html[data-theme='dark'] [class*='AboutOutside-sc-'],
  html[data-theme='dark'] [class*='AboutContact-sc-'] {
    color: var(--theme-text) !important;
  }
`;

export const getStoredTheme = () => {
  if (typeof window === 'undefined') return 'light';

  if (localStorage.getItem(LEGACY_THEME_KEY)) {
    const legacyTheme = localStorage.getItem(LEGACY_THEME_KEY);
    if (!localStorage.getItem(THEME_STORAGE_KEY) && legacyTheme) {
      localStorage.setItem(THEME_STORAGE_KEY, legacyTheme);
    }
    localStorage.removeItem(LEGACY_THEME_KEY);
  }

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;

  const prefersDark =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  return prefersDark ? 'dark' : 'light';
};

export const applyThemeFavicon = theme => {
  if (typeof document === 'undefined') return;

  const resolved = theme === 'dark' ? 'dark' : 'light';
  const href = FAVICON_PATHS[resolved];

  let link = document.querySelector('link[data-theme-favicon]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.setAttribute('data-theme-favicon', '');
    document.head.appendChild(link);
  }

  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
  document.head.appendChild(link);

  let themeColorMeta = document.querySelector('meta[data-theme-color]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    themeColorMeta.setAttribute('data-theme-color', '');
    document.head.appendChild(themeColorMeta);
  }

  themeColorMeta.content = THEME_COLOR[resolved];
};

export const applyThemeToDocument = theme => {
  if (typeof document === 'undefined') return;
  const resolved = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.setAttribute('data-theme', resolved);
  document.documentElement.style.colorScheme = resolved;
  applyThemeFavicon(resolved);
};

export const persistTheme = theme => {
  if (typeof window === 'undefined') return;
  const resolved = theme === 'dark' ? 'dark' : 'light';
  localStorage.setItem(THEME_STORAGE_KEY, resolved);
  applyThemeToDocument(resolved);
};

export const themeInitScript = `
(function () {
  try {
    var key = '${THEME_STORAGE_KEY}';
    var legacy = '${LEGACY_THEME_KEY}';
    if (localStorage.getItem(legacy)) {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, localStorage.getItem(legacy));
      }
      localStorage.removeItem(legacy);
    }
    var theme = localStorage.getItem(key);
    if (theme !== 'dark' && theme !== 'light') {
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    // Arm hero entrance animation before paint so the LCP text never waits on hydration.
    document.documentElement.classList.add('hero-armed');
    var favicon = document.querySelector('link[data-theme-favicon]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/svg+xml';
      favicon.setAttribute('data-theme-favicon', '');
      document.head.appendChild(favicon);
    }
    favicon.href = theme === 'dark' ? '/favicon-dark.svg' : '/favicon-light.svg';
    var themeColor = document.querySelector('meta[data-theme-color]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      themeColor.setAttribute('data-theme-color', '');
      document.head.appendChild(themeColor);
    }
    themeColor.content = theme === 'dark' ? '#000000' : '#ffffff';
  } catch (e) {}
})();
`;

export const themeInitStyle = `
  html { color-scheme: light dark; }
  html[data-theme='dark'] {
    ${cssVarBlock('dark')}
    background-color: var(--theme-bg);
    color: var(--theme-text);
    color-scheme: dark;
  }
  html[data-theme='light'] {
    ${cssVarBlock('light')}
    background-color: var(--theme-bg);
    color: var(--theme-text);
    color-scheme: light;
  }
  html[data-theme='dark'] body,
  html[data-theme='light'] body {
    background-color: var(--theme-bg);
    color: var(--theme-text);
  }
  #___gatsby,
  #gatsby-focus-wrapper {
    background-color: var(--theme-bg);
    color: var(--theme-text);
  }
  ${preHydrationOverrides}
`;
